import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Stack,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import LinkIcon from '@mui/icons-material/Link';
import { useNotify, useRefresh } from 'react-admin';
import { getToken, getRefreshToken } from '../authentication/authProvider';

const PAPERMAN_API_URL = process.env.REACT_APP_PAPERMAN_API_URL;

interface ContactOption {
  label: string;
  name: string;
  email: string;
}

interface OasCancellationSendModalProps {
  open: boolean;
  onClose: () => void;
  funeralId: number;
  /** Used to build the signing link for "Copy link". */
  funeralUuid?: string;
  /** Called after a successful send so the parent can refresh/close. */
  onSent?: () => void;
  /** ISP-1201 edition shown in the review modal; the send prints this one. */
  formLanguage?: 'en' | 'fr';
}

// Review-and-send dialog for the OAS cancellation signing email. Loads the
// resolved recipient + the exact region-specific draft from the backend so the
// agent can see (and adjust) who it's going to and what it says before it
// fires. Mirrors CppBenefitsSendModal, but single-signer.
const OasCancellationSendModal: React.FC<OasCancellationSendModalProps> = ({
  open,
  onClose,
  funeralId,
  funeralUuid,
  onSent,
  formLanguage,
}) => {
  const notify = useNotify();
  const refresh = useRefresh();

  const [loadingDraft, setLoadingDraft] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [to, setTo] = useState('');
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [contacts, setContacts] = useState<ContactOption[]>([]);

  // Load the draft each time the modal opens (stale state is reset).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingDraft(true);
    setError(null);
    void (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${PAPERMAN_API_URL}oas-cancellation/draft/${funeralId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Refresh-Token': await getRefreshToken(),
          },
        });
        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          throw new Error(e?.message || `Failed to load draft (${res.status})`);
        }
        const d = await res.json();
        if (cancelled) return;
        setTo(d.to || '');
        setName(d.name || '');
        setSubject(d.subject || '');
        setBody(d.body || '');
        setContacts(Array.isArray(d.contacts) ? d.contacts : []);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Failed to load draft');
      } finally {
        if (!cancelled) setLoadingDraft(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, funeralId]);

  const post = useCallback(
    async (skipEmails: boolean) => {
      const token = await getToken();
      const res = await fetch(`${PAPERMAN_API_URL}oas-cancellation/send/${funeralId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'Refresh-Token': await getRefreshToken(),
        },
        body: JSON.stringify({
          skipEmails,
          formLanguage,
          to: to.trim(),
          name: name.trim(),
          subject: subject.trim(),
          body,
        }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e?.message || `Send failed (${res.status})`);
      }
      return res.json();
    },
    [funeralId, formLanguage, to, name, subject, body],
  );

  const handleSend = async () => {
    if (!to.trim()) return;
    setSending(true);
    try {
      await post(false);
      notify('OAS cancellation sent to the family for signature', { type: 'success' });
      refresh();
      onSent?.();
      onClose();
    } catch (err: any) {
      console.error('OAS send error:', err);
      notify(err?.message || 'Failed to send OAS cancellation form', { type: 'error' });
    } finally {
      setSending(false);
    }
  };

  // Generate the submission but skip the email, then copy the Aura signing link
  // for sending through another channel (or testing).
  const handleCopyLink = async () => {
    if (!to.trim()) return;
    setSending(true);
    try {
      const data = await post(true);
      const slug = data?.slug;
      if (!slug) throw new Error('No signing link returned');
      const rawBase =
        process.env.REACT_APP_AURA_APP_URL ||
        (typeof window !== 'undefined' && window.location.hostname === 'localhost'
          ? 'http://localhost:3005'
          : 'https://cleocremation.com');
      const trimmed = rawBase.replace(/\/$/, '');
      const auraBase = /\/app$/i.test(trimmed) ? trimmed : `${trimmed}/app`;
      const url = `${auraBase}/${funeralUuid}/oas-cancellation/${slug}`;
      try {
        await navigator.clipboard.writeText(url);
        notify('Signing link copied to clipboard', { type: 'success' });
      } catch {
        notify(`Signing link: ${url}`, { type: 'info', autoHideDuration: 15000 });
      }
      refresh();
      onSent?.();
      onClose();
    } catch (err: any) {
      console.error('OAS copy-link error:', err);
      notify(err?.message || 'Failed to generate signing link', { type: 'error' });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Send OAS Cancellation for Signature</DialogTitle>
      <DialogContent dividers>
        {loadingDraft ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <Stack spacing={1.5}>
            <Typography variant="caption" color="text.secondary">
              The signing link is added to the email automatically. Review the recipient and message before sending.
            </Typography>

            {contacts.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {contacts.map((c) => (
                  <Chip
                    key={`${c.label}-${c.email}`}
                    label={`${c.label}: ${c.name || c.email}`}
                    size="small"
                    onClick={() => {
                      setTo(c.email);
                      setName(c.name);
                    }}
                    disabled={sending || !c.email}
                  />
                ))}
              </Stack>
            )}

            <Stack direction="row" spacing={1}>
              <TextField
                label="Recipient name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={sending}
                size="small"
                sx={{ flex: 1 }}
              />
              <TextField
                label="To (email)"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                disabled={sending}
                size="small"
                type="email"
                required
                error={!to.trim()}
                sx={{ flex: 1.5 }}
              />
            </Stack>

            <TextField
              label="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={sending}
              fullWidth
              size="small"
            />
            <TextField
              label="Message"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={sending}
              fullWidth
              multiline
              minRows={8}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>
          Cancel
        </Button>
        <Button
          onClick={handleSend}
          variant="contained"
          color="primary"
          disabled={sending || loadingDraft || !to.trim()}
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
        >
          Send
        </Button>
        <Button
          onClick={handleCopyLink}
          variant="outlined"
          color="primary"
          disabled={sending || loadingDraft || !to.trim()}
          startIcon={<LinkIcon />}
        >
          Copy link
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OasCancellationSendModal;
